<template>
	<div class="stage">
		<page-header title="Phone Numbers" />

		<filter-bar
			:show-filters="showFilters"
			:has-export="true"
			:show-add-button="$global.hasUserRole(userRoles, 'phonenumbers:create')"
			add-button-label="Add Phone Number"
			@toggle-filters="showFilters = !showFilters"
			@reset-filters="resetSearchParams"
			@export="exportToCsv"
			@add-new="routeTo('/phonenumbers/new')"
		>
			<template #additional-buttons>
				<li>
					<a
						v-if="
							$global.hasUserRole(userRoles, 'phonenumbers:create') &&
							(searchParams.selectedTenant || tenants.length === 1)
						"
						class="jmiBtn orange withIcon"
						@click="triggerFileInput"
					>
						<span class="fa-light fa-file-import"></span>Import
					</a>
				</li>
				<li>
					<a
						v-if="$global.hasUserRole(userRoles, 'phonenumbers:create')"
						class="jmiBtn grey withIcon"
						@click="showPortingModal = true"
					>
						<span class="fa-light fa-right-left"></span>Port Number
					</a>
				</li>
				<input ref="fileInput" type="file" accept=".csv" class="hidden" @change="handleFileImport" />
			</template>
		</filter-bar>

		<filter-section
			:show="showFilters"
			:filters="searchParams"
			:search-term="searchField"
			:tenants="tenants"
			:accounts="accounts"
			date-label="Date Added"
			:allow-tenants="true"
			tenant-label="Tenant Numbers"
			show-tenant-filter
			show-account-filter
			show-date-filter
			show-search-filter
			search-label="Number"
			@update:filters="searchParams = $event"
			@update:search-term="searchField = $event"
			@tenant-change="$global.setSelectedTenant"
			@account-change="handleAccountChange"
		>
			<FormField class="w-[calc(50%-0.25rem)] lg:w-[calc(25%-0.375rem)]" label="Zone">
				<searchable-dropdown
					default-option-text="All Zones"
					placeholder="All Zones"
					:options="availableZones"
					:model-value="searchParams.selectedZone"
					:multi-select="false"
					@change="searchParams.selectedZone = $event"
				/>
			</FormField>
		</filter-section>

		<data-view
			:columns="columns"
			:sort-column="sortColumn"
			:reverse="reverse"
			:loading="loading"
			:label="'Phone Numbers'"
			:total-items="totalNumbers"
			:page-size="pageSize"
			:current-page="currentPage"
			:pages="pages"
			@sort="setSort"
			@update:page-size="changePageSize"
			@page-change="changePage"
		>
			<template #table-view>
				<generic-table
					:items="numbers"
					:columns="columns"
					item-type="phone numbers"
					base-route="phonenumbers"
					:can-edit="$global.hasUserRole(userRoles, 'phonenumbers:update')"
					:view-mode="'table'"
					:empty-state-config="{
						icon: 'fa-light fa-phone',
						title: 'No phone numbers yet',
						description: 'Start by adding your first phone number or importing from a CSV file.',
						action: $global.hasUserRole(userRoles, 'phonenumbers:create')
							? {
									label: 'Add Phone Number',
									icon: 'fa-light fa-plus',
									handler: () => routeTo('/phonenumbers/new')
								}
							: null
					}"
					:actions="[
						{
							handler: (number) => routeTo(`/phonenumbers/${number.id}`),
							icon: 'fa-light fa-pencil',
							label: 'Edit'
						},
						{
							handler: (number, event) => {
								if (!number?.appConfig?.mobile_sim?.simId) return
								const url = `/mobilesims/${number.appConfig.mobile_sim.simId}`
								if (event?.ctrlKey || event?.metaKey) {
									const baseUrl = window.location.origin
									window.open(baseUrl + url, '_blank')
								} else {
									routeTo(url)
								}
							},
							icon: 'fa-light fa-sim-card',
							label: 'View SIM',
							show: (number) => number.app === 'mobile_sim' && number.appConfig?.mobile_sim?.simId
						},
						{
							permission: 'phonenumbers:delete',
							handler: deleteNumber,
							icon: 'fa-light fa-delete-left',
							label: 'Delete'
						}
					]"
				>
					<template #number="{ item }">
						<div class="flex items-center gap-2">
							<img
								v-if="getCountryFlagEmoji(item.zone_name)"
								:src="getCountryFlagEmoji(item.zone_name)"
								:alt="getCountryFlagName(item.zone_name) + ' Flag Icon'"
								class="h-4 w-4"
							/>
							<p>{{ item.number }}</p>
						</div>
					</template>
					<template #tenant="{ item }">
						{{ item.tenant?.companyName || 'Unassigned' }}
					</template>
					<template #account="{ item }">
						{{ item.account?.name || 'Unassigned' }}
					</template>
					<template #app="{ item }">
						{{ $global.prettyPrint(item.app) || 'None' }}
					</template>
					<template #appConfig="{ item }">
						<div v-html="showConfig(item)"></div>
					</template>
					<template #mobile-view="{ item }">
						<div class="flex w-full flex-col">
							<div class="text-md absolute right-4 top-4 block max-w-[160px]">
								<p class="text-right font-semibold uppercase text-gray-400">Account</p>
								<p class="truncate text-right">{{ item.account?.name || 'Unassigned' }}</p>
							</div>

							<div class="flex flex-col gap-2">
								<div class="flex flex-col">
									<p class="font-semibold uppercase text-gray-500">Number:</p>
									<div class="flex items-center gap-2">
										<img
											v-if="getCountryFlagEmoji(item.zone_name)"
											:src="getCountryFlagEmoji(item.zone_name)"
											:alt="getCountryFlagName(item.zone_name) + ' Flag Icon'"
											class="h-4 w-4"
										/>
										<p>{{ item.number }}</p>
									</div>
								</div>
								<div class="flex flex-col">
									<p class="font-semibold uppercase text-gray-500">Tenant:</p>
									<p>{{ item.tenant?.companyName || 'Unassigned' }}</p>
								</div>
								<div class="flex flex-col">
									<p class="font-semibold uppercase text-gray-500">Date Added:</p>
									<p>{{ $global.dateFormat(item.createdAt) }}</p>
								</div>
							</div>
						</div>
					</template>
				</generic-table>
			</template>
		</data-view>

		<PortingWizard
			title="Port a Number"
			:open="showPortingModal"
			:user-roles="userRoles"
			:tenants="tenants"
			:accounts-prop="accounts"
			@update:open="showPortingModal = $event"
			@complete="handlePortingComplete"
		/>
	</div>
</template>

<script>
import phoneNumbersApi from '../scripts/phonenumbers/phonenumbers.api'
import accountsApi from '../scripts/accounts/accounts.api'
import tariffCodesApi from '../scripts/tariffcodes/tariffcodes.api'
import sipTrunksApi from '../scripts/siptrunks/siptrunks.api'
import filesApi from '../scripts/files/files.api'
import flowsApi from '../scripts/flows/flows.api'
import mobileSimsApi from '../scripts/mobilesims/mobilesims.api'
import { router } from '../router'
import moment from 'moment'
import debounce from 'lodash.debounce'
import axios from 'axios'
import countryFlagEmojis from '@/assets/country-flag-emojis.json'
import PortingWizard from '../components/common/PortingWizard.vue'

export default {
	components: {
		PortingWizard
	},
	inject: ['user', 'userRoles', 'tenants'],
	data() {
		return {
			columns: [
				{
					key: 'number',
					label: 'Number',
					sortable: true,
					class: 'col-xs-12 col-sm-3 col-md-2 col-lg-2'
				},
				{
					key: 'zone_name',
					label: 'Zone',
					sortable: true,
					class: 'col-sm-3 col-md-1 col-lg-1 hideTablet'
				},
				{
					key: 'tenant',
					label: 'Tenant',
					class: 'col-xs-12 col-sm-3 col-md-2 col-lg-2 hideTabletLandscape'
				},
				{
					key: 'app',
					label: 'App',
					sortable: true,
					class: 'col-xs-12 col-sm-3 col-md-1 col-lg-1'
				},
				{
					key: 'appConfig',
					label: 'App Config',
					sortable: true,
					class: 'col-xs-12 col-sm-3 col-md-2 col-lg-2'
				},
				{
					key: 'account',
					label: 'Account',
					class: 'col-xs-12 col-sm-3 col-md-2 col-lg-2'
				},
				{
					key: 'createdAt',
					label: 'Date Added',
					sortable: true,
					class: 'col-xs-12 col-sm-2 col-md-2 col-lg-2 hideTablet'
				}
			],
			showFilters: true,
			searchParams: {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: '',
				selectedAccount: null,
				selectedZone: ''
			},
			numbers: [],
			accounts: [],
			totalNumbers: 0,
			pageSize: 25,
			currentPage: 1,
			sortColumn: 'number',
			reverse: false,
			loading: true,
			searchField: '',
			dates: [],
			debouncedWatch: null,
			sipTrunks: [],
			files: [],
			flows: [],
			mobileSims: [],
			configCache: new Map(),
			isInitialLoad: true,
			availableZones: [],
			showPortingModal: false,
			porting: {
				number: '',
				currentProvider: '',
				accountNumber: '',
				authorizedPerson: '',
				businessName: '',
				pin: '',
				authorized: false,
				selectedTenant: '',
				selectedAccount: '',
				app: '',
				title: 'Mr',
				firstName: '',
				lastName: '',
				companyName: '',
				propertyName: '',
				street: '',
				townCity: '',
				postcode: ''
			}
		}
	},

	computed: {
		pages() {
			const numPages = Math.ceil(this.totalNumbers / this.pageSize) || 1
			return Array.from({ length: numPages }, (_, i) => i)
		},
		selectedTenantName() {
			if (!this.searchParams.selectedTenant) return 'All Tenants'
			const tenant = this.tenants?.find((t) => t.id === parseInt(this.searchParams.selectedTenant))
			return tenant?.companyName || 'All Tenants'
		},
		isStep1Valid() {
			return (
				this.porting.number.trim() !== '' &&
				this.porting.currentProvider.trim() !== '' &&
				this.porting.accountNumber.trim() !== ''
			)
		},
		isStep2Valid() {
			return (
				this.porting.firstName.trim() !== '' &&
				this.porting.lastName.trim() !== '' &&
				this.porting.propertyName.trim() !== '' &&
				this.porting.street.trim() !== '' &&
				this.porting.townCity.trim() !== '' &&
				this.porting.postcode.trim() !== ''
			)
		},
		isStep3Valid() {
			return (
				this.porting.authorizedPerson.trim() !== '' &&
				this.porting.businessName.trim() !== '' &&
				this.porting.authorized === true
			)
		},
		isStep4Valid() {
			return this.porting.selectedTenant !== '' && this.porting.selectedAccount !== '' && this.porting.app !== ''
		}
	},

	watch: {
		searchField(newValue, oldValue) {
			this.debouncedWatch(newValue, oldValue)
		},

		searchParams: {
			deep: true,
			handler() {
				if (!this.isInitialLoad) {
					this.reloadNumbers()
				}
			}
		}
	},

	async created() {
		if (this.$global.getSelectedTenant()) {
			this.searchParams.selectedTenant = this.$global.getSelectedTenant()
			this.porting.selectedTenant = this.$global.getSelectedTenant()
		}

		this.debouncedWatch = debounce(this.reloadNumbers, 1000)

		try {
			this.loading = true
			const params = this.getSearchParams()
			if (this.$route.query?.globalSearch) {
				params.searchTerm = this.$route.query.globalSearch
			}

			const [
				{
					data: { results: tariffCodes }
				},
				{
					data: { results: sipTrunks }
				},
				{
					data: { results: files }
				},
				{
					data: { results: flows }
				},
				{
					data: { results: accounts }
				},
				{
					data: { results: numbers, count: totalNumbers }
				},
				{
					data: { results: mobileSims }
				}
			] = await Promise.all([
				tariffCodesApi.getAllZoneNames(),
				sipTrunksApi.getAll(),
				filesApi.getAll(),
				flowsApi.getAll(),
				accountsApi.getAll({ limit: 2500 }),

				phoneNumbersApi.getAll(params),
				mobileSimsApi.getAll()
			])

			this.availableZones = tariffCodes
			this.sipTrunks = sipTrunks
			this.files = files
			this.flows = flows
			this.accounts = accounts
			this.numbers = numbers
			this.totalNumbers = totalNumbers
			this.mobileSims = mobileSims
		} catch (error) {
			console.error(error)
		} finally {
			this.loading = false
			this.isInitialLoad = false
		}
	},

	methods: {
		setSort(column, reverse) {
			if (this.sortColumn !== column) {
				this.reverse = false
			} else {
				this.reverse = reverse
			}
			this.sortColumn = column
			this.reloadNumbers()
		},

		changePageSize(size) {
			this.pageSize = size
			const numPages = Math.ceil(this.totalNumbers / size) || 1
			this.currentPage = Math.min(this.currentPage, numPages)
			this.$nextTick(() => {
				this.reloadNumbers()
			})
		},

		changePage(page) {
			this.currentPage = page
			this.reloadNumbers()
		},

		resetSearchParams() {
			this.searchParams = {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: '',
				selectedAccount: null,
				selectedZone: ''
			}
			this.dates = []
			this.searchField = ''
		},

		getSearchParams() {
			const params = {
				limit: this.pageSize,
				skip: this.pageSize * (this.currentPage - 1),
				where: {},
				order: {
					[this.sortColumn]: this.reverse ? 'DESC' : 'ASC'
				}
			}

			if (this.searchParams.dateAddedFrom) {
				params.where.createdAtFrom = `${moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD')} 00:00:00`
				params.where.createdAtTo = this.searchParams.dateAddedTo
					? `${moment(this.searchParams.dateAddedTo).format('YYYY-MM-DD')} 23:59:59`
					: `${moment(this.searchParams.dateAddedFrom).format('YYYY-MM-DD')} 23:59:59`
			}

			if (this.searchParams.selectedTenant) {
				params.where.selectedTenant = this.searchParams.selectedTenant
			}

			if (this.searchParams.selectedAccount) {
				params.where.selectedAccount = this.searchParams.selectedAccount
			}

			if (this.searchParams.selectedZone) {
				params.where.zone_name = this.searchParams.selectedZone
			}

			if (this.$route.query?.numberport_id) {
				params.numberport_id = this.$route.query.numberport_id
			}

			return params
		},

		async reloadNumbers(globalSearch) {
			try {
				this.loading = true
				let params = this.getSearchParams()

				if (globalSearch) {
					params = { ...params, searchTerm: globalSearch }
				} else if (this.searchField) {
					params.searchTerm = this.searchField
				}

				const response = await phoneNumbersApi.getAll(params)
				this.totalNumbers = response.data.count
				this.numbers = response.data.results

				const maxPages = Math.ceil(this.totalNumbers / this.pageSize) || 1
				if (this.currentPage > maxPages) {
					this.currentPage = 1
					this.reloadNumbers(globalSearch)
					return
				}
			} catch (err) {
				console.error(err)
			} finally {
				this.loading = false
			}
		},

		async deleteNumber(number) {
			if (number) {
				this.$swal({
					title: 'Delete Phone Number',
					text: `Would you like to delete phone number '${number.number}'?`,
					showCancelButton: true,
					confirmButtonText: 'Delete',
					cancelButtonText: 'Cancel',
					showDenyButton: false,
					denyButtonText: 'Cancel'
				}).then((result) => {
					if (result.isConfirmed)
						phoneNumbersApi
							.delete(number.id)
							.then((response) => {
								if (response && response.data && response.data.success) this.reloadNumbers()
								else
									this.$swal({
										title: 'Error',
										icon: 'error',
										confirmButtonText: 'Confirm',
										text: response?.data?.error || 'Unknown error occurred, please contact support.'
									})
							})
							.catch(console.error)
				})
			}
		},

		routeTo(routeName) {
			router.push(routeName)
		},

		async exportToCsv() {
			try {
				const searchParams = { ...this.getSearchParams(), exportCsv: true }
				const response = await axios.get(`${window.dtServerUrl}/phonenumbers`, { params: searchParams })

				const blob = new Blob([response.data], { type: response.headers.get('content-type') })
				const link = document.createElement('a')
				link.href = window.URL.createObjectURL(blob)
				link.download = 'teleflow-phonenumbers.csv'
				link.click()
			} catch (error) {
				console.error(error)
			}
		},
		showConfig(phoneNumber) {
			const cacheKey = `${phoneNumber.app}-${JSON.stringify(phoneNumber.appConfig)}`
			if (this.configCache.has(cacheKey)) {
				return this.configCache.get(cacheKey)
			}

			if (phoneNumber.app && phoneNumber.appConfig && phoneNumber.appConfig[phoneNumber.app]) {
				const currentConfig = phoneNumber.appConfig[phoneNumber.app]
				const config = this.getConfigDisplay(phoneNumber.app, currentConfig)
				this.configCache.set(cacheKey, config)
				return config
			}
			return ''
		},
		getConfigDisplay(app, currentConfig) {
			switch (app) {
				case 'speaking_clock':
				case 'diagnostics':
				case 'tenant_balance_check':
				case 'account_balance_check':
				case 'proxy':
					return ''
				case 'play_prompt': {
					let file = this.files.find((file) => file.id === currentConfig.fileId)
					return file ? file.name : ''
				}
				case 'dial_thru': {
					let file = this.files.find((file) => file.id === currentConfig.welcomePromptFileId)
					return file ? file.name : ''
				}
				case 'prompt_then_forward': {
					let file = this.files.find((file) => file.id === currentConfig.fileId)
					return file ? file.name : ''
				}
				case 'call_forwarding':
					return currentConfig.destination
				case 'sip_trunk': {
					let sipTrunk = this.sipTrunks.find((sipTrunk) => sipTrunk.id === parseInt(currentConfig.trunkId))
					return sipTrunk ? sipTrunk.username : ''
				}
				case 'mobile_sim': {
					if (!currentConfig.simId) return 'No SIM configured'
					let mobileSim = this.mobileSims.find((mobileSim) => mobileSim.id === parseInt(currentConfig.simId))
					if (mobileSim) {
						const voicemailEnabled = currentConfig.voicemail?.enabled
						const voicemailIcon = voicemailEnabled
							? '<i class="fa-light fa-voicemail text-green-600 ml-1" title="Voicemail Enabled"></i>'
							: ''
						return `<span>${mobileSim.number}${mobileSim.description ? ` - ${mobileSim.description}` : ''} ${voicemailIcon}</span>`
					}
					return 'Loading...'
				}
				case 'flows': {
					let flow = this.flows.find((flow) => flow.id === parseInt(currentConfig.flowId))
					return flow ? flow.name : ''
				}
				default:
					return ''
			}
		},
		handleAccountChange(value) {
			this.searchParams.selectedAccount = value
			this.reloadNumbers()
		},
		getCountryFlagEmoji(countryCode) {
			if (countryCode?.indexOf('UK') === 0) countryCode = 'United Kingdom'

			if (countryCode)
				return (
					countryFlagEmojis.find((c) => countryCode.toLowerCase().indexOf(c.name.toLowerCase()) > -1)
						?.image || ''
				)
			return ''
		},
		getCountryFlagName(countryCode) {
			if (countryCode?.indexOf('UK') === 0) countryCode = 'United Kingdom'

			if (countryCode)
				return (
					countryFlagEmojis.find((c) => countryCode.toLowerCase().indexOf(c.name.toLowerCase()) > -1)?.name ||
					''
				)
			return ''
		},

		getTenantName(tenantId) {
			if (!tenantId) return 'Not selected'
			const tenant = this.tenants.find((t) => t.id === parseInt(tenantId))
			return tenant ? tenant.companyName : 'Unknown'
		},

		getAccountName(accountId) {
			if (!accountId) return 'Not selected'
			const account = this.accounts.find((a) => a.id === accountId)
			return account ? account.name : 'Unknown'
		},

		handlePortingComplete(portingData) {
			this.$swal({
				title: 'Port Request Submitted',
				text: `Your request to port ${portingData.number} has been submitted. The porting process should take 7-10 working days to complete.`,
				icon: 'success'
			})
		}
	}
}
</script>
